import React from 'react';

/**
 * HowTo Schema Markup Generator
 * Generates JSON-LD structured data for how-to guides and tutorials
 */

export interface HowToStep {
  name: string;
  text: string;
  image?: string;
  url?: string;
}

export interface HowToSchemaData {
  name: string;
  description: string;
  image?: string;
  totalTime?: string; // ISO 8601 duration format (e.g., "PT30M" for 30 minutes)
  estimatedCost?: {
    currency: string;
    value: string;
  };
  supply?: string[];
  tool?: string[];
  steps: HowToStep[];
}

export function generateHowToSchema(data: HowToSchemaData) {
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: data.name,
    description: data.description,
  };

  if (data.image) {
    schema.image = data.image;
  }

  if (data.totalTime) {
    schema.totalTime = data.totalTime;
  }

  if (data.estimatedCost) {
    schema.estimatedCost = {
      '@type': 'MonetaryAmount',
      currency: data.estimatedCost.currency,
      value: data.estimatedCost.value,
    };
  }

  if (data.supply && data.supply.length > 0) {
    schema.supply = data.supply.map(item => ({
      '@type': 'HowToSupply',
      name: item,
    }));
  }

  if (data.tool && data.tool.length > 0) {
    schema.tool = data.tool.map(item => ({
      '@type': 'HowToTool',
      name: item,
    }));
  }

  schema.step = data.steps.map((step, index) => {
    const stepSchema: any = {
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    };

    if (step.image) {
      stepSchema.image = step.image;
    }

    if (step.url) {
      stepSchema.url = step.url;
    }

    return stepSchema;
  });

  return schema;
}

/**
 * Generate HowTo JSON-LD script tag
 */
export function HowToSchemaScript({ data }: { data: HowToSchemaData }) {
  const schema = generateHowToSchema(data);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
